import React, { useEffect, useRef } from 'react';
import { LogOut, X } from 'lucide-react';

interface LogoutModalProps {
  open: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

const t = {
  primary: '#7c3aed',
  danger: '#ef4444',
  dangerLight: 'rgba(239,68,68,0.08)',
  surface: '#ffffff',
  border: '#e9e5f5',
  overlay: 'rgba(30,27,75,0.55)',
  text: { primary: '#1e1b4b', secondary: '#6b7280', muted: '#9ca3b8' },
};

export default function LogoutModal({ open, onConfirm, onCancel }: LogoutModalProps) {
  const cancelRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (open) {
      cancelRef.current?.focus();
      const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onCancel(); };
      document.addEventListener('keydown', onKey);
      return () => document.removeEventListener('keydown', onKey);
    }
  }, [open, onCancel]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center p-4"
      style={{ backgroundColor: t.overlay, backdropFilter: 'blur(6px)' }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="logout-title"
      aria-describedby="logout-desc"
      onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}
    >
      <div
        className="relative w-full max-w-sm rounded-2xl p-6 shadow-2xl"
        style={{
          backgroundColor: t.surface,
          border: `1px solid ${t.border}`,
          animation: 'modalIn 200ms cubic-bezier(0.34,1.56,0.64,1) both',
        }}
      >
        {/* Close button */}
        <button
          type="button"
          onClick={onCancel}
          className="absolute top-4 right-4 p-1.5 rounded-lg transition-colors"
          style={{ color: t.text.muted, background: 'transparent', border: 'none', cursor: 'pointer' }}
          aria-label="Fermer"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Icon */}
        <div
          className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 mx-auto"
          style={{ backgroundColor: t.dangerLight }}
        >
          <LogOut className="w-6 h-6" style={{ color: t.danger }} aria-hidden="true" />
        </div>

        {/* Text */}
        <h2 id="logout-title" className="text-[17px] font-bold mb-1.5 text-center" style={{ color: t.text.primary }}>
          Se déconnecter ?
        </h2>
        <p id="logout-desc" className="text-[13px] leading-relaxed mb-6 text-center" style={{ color: t.text.secondary }}>
          Vous allez quitter votre session. Vos données non sauvegardées seront perdues.
        </p>

        {/* Actions */}
        <div className="flex gap-3">
          <button
            ref={cancelRef}
            type="button"
            onClick={onCancel}
            className="flex-1 py-2.5 rounded-xl text-[13px] font-medium transition-all duration-150"
            style={{
              border: `1px solid ${t.border}`,
              background: '#f9f8ff',
              color: t.text.primary,
              cursor: 'pointer',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = t.primary; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = t.border; }}
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="flex-1 py-2.5 rounded-xl text-[13px] font-semibold transition-all duration-150 flex items-center justify-center gap-2"
            style={{
              background: t.danger,
              color: '#ffffff',
              border: 'none',
              cursor: 'pointer',
              boxShadow: '0 4px 12px rgba(239,68,68,0.3)',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 6px 16px rgba(239,68,68,0.4)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(239,68,68,0.3)'; }}
          >
            <LogOut className="w-4 h-4" aria-hidden="true" />
            Déconnecter
          </button>
        </div>
      </div>

      <style>{`
        @keyframes modalIn {
          from { opacity: 0; transform: scale(0.92) translateY(8px); }
          to   { opacity: 1; transform: scale(1) translateY(0); }
        }
      `}</style>
    </div>
  );
}
